import { FunctionComponent, useState, useEffect, useRef } from 'react';
import Button, { ButtonProps } from '../../Input/Button/Button';
import DropDown from '../../Compound/DropDown/DropDown';

import styles from './OverflowButton.module.scss';

export type ModifiedButtonProps = ButtonProps & {
  windowBreakPoint: number;
};

export type OverflowButtonProps = {
  buttons: ModifiedButtonProps[];
  dropDownButtonStyle: ButtonProps;
  verticalPosition?: 'top' | 'bottom';
  horizontalPosition?: 'left' | 'right';
  width?: string;
  height?: string;
  dropdownButtonHeight?: string;
  hasNoPadding?: boolean;
  fontUnit?: 'em' | 'px';
  textSize?: number;
};

type FC = FunctionComponent<OverflowButtonProps>;

const OverflowButton: FC = (props: OverflowButtonProps) => {
  const {
    buttons,
    dropDownButtonStyle,
    verticalPosition = 'bottom',
    horizontalPosition = 'left',
    width,
    height,
    dropdownButtonHeight,
    hasNoPadding,
    fontUnit = 'em',
    textSize = 1,
  } = props;
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Function to handle window resize
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    // Add event listener for window resize
    window.addEventListener('resize', handleResize);

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []); // Empty dependency array ensures the effect runs only once

  // Filter buttons based on width and the specified breakpoint
  const visibleButtons = buttons.filter((button) => button.windowBreakPoint <= windowWidth);
  const dropdownButtons = buttons.filter((button) => button.windowBreakPoint > windowWidth);
  const isDropdownVisible = dropdownButtons.length > 0;

  return (
    <div className={styles.overflowContainer} ref={containerRef}>
      {visibleButtons.map((button) => (
        <Button {...button} key={button.id} />
      ))}
      <div
        className={[
          styles.dropdownContainer,
          styles[verticalPosition],
          styles[horizontalPosition],
          styles[getNoPaddingClass()],
        ].join(' ')}
      >
        {isDropdownVisible && (
          <Button
            {...dropDownButtonStyle}
            key={dropDownButtonStyle.id}
            width={width}
            height={height}
            onClick={() => {
              setIsMenuVisible(!isMenuVisible);
            }}
          />
        )}
        {isDropdownVisible && (
          <DropDown isMenuVisible={isMenuVisible} onClick={setIsMenuVisible}>
            {dropdownButtons.map((button) => (
              <Button
                key={button.id}
                id={button.id}
                label={button.label}
                color="None"
                textSize={textSize}
                fontUnit={fontUnit}
                width="100%"
                height={dropdownButtonHeight}
                noShadow
                onClick={button.onClick}
              />
            ))}
          </DropDown>
        )}
      </div>
    </div>
  );

  function getNoPaddingClass() {
    return hasNoPadding ? 'noPadding' : '';
  }
};

export default OverflowButton;
